import { Grid } from '@mui/material'
import { Text, View } from '@react-pdf/renderer'
import React from 'react'
import { parseFormLabelText } from '../common/Common'
import { FormElementTitle } from '../common/FormElementTitle'
import { formComponentTypes, formTypeToComponentTypeKey } from '../components/formComponentTypes'
import {
  formElementsWithoutInput,
  formPDFElementsWithoutDefaultTitle
} from '../editor/GroupElement'
import { extractFormFieldDetails, formItemPadding, getMainConnected } from '../Form'
import { checkAltLabel, isConditionMet } from '../formHelpersConditions'
import { getPDFLabelFromConditions } from '../formUtils'
import { parseLabel } from '../parseLabel'
import { AdjustPdfProps } from '../pdf-components/AdjustPdfProps'
import { FormElementTitlePDF } from '../pdf-components/FormElementTitlePDF'
import { defaultFormPdfFontFamily, inuktitutFormPdfFontFamily } from '../pdf-components/FormPdf'
import { pdfDefaultFontSize } from '../pdf-components/PDFCommon'
import { FormElementForReadOnly } from './FormElementForReadOnly'

export const FormElement = ({
  xs = 12,
  item,
  langVersion,
  connectedMap,
  describeMap,
  muBag,
  index,
  formViewType = 'editable',
  disabledIds,
  printView,
  renderPrint,
  values,
  idToRenderIndex = {},
  pdfDocument,
  renderElementsWithNumbering,
  itemPadding,
  isOnlyOneInSection,
  ...props
}) => {
  const pdfView = formViewType.includes('pdf')
  const type = item.elementType
  const elementData = formComponentTypes[type]
  const {
    labelsWidth,
    padding = {},
    itemsSpacing,
    title = {},
    conditions = [],
    typeProps,
    tooltip,
    helpText
  } = item

  const { options, isConnected, showFieldLabel, required, allowPDFWrap, pageBreak } = typeProps
  const paddingStyles = {}
  const paddingKeys = ['paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom']
  paddingKeys.forEach((key) => {
    let toSet = formItemPadding
    if (pdfView) {
      if (key === 'paddingTop' || key === 'paddingBottom') {
        toSet = 6
      } else {
        toSet = 0
        if (key === 'paddingRight' && itemPadding) {
          toSet = 6
        }
      }
    }
    if (padding[key]) {
      toSet = Number(padding[key])
    }
    if (itemsSpacing || itemsSpacing === 0) {
      toSet = Number(itemsSpacing)
    }
    paddingStyles[key] = toSet
  })

  if (type && item.id && elementData) {
    const renderComponent = elementData[formTypeToComponentTypeKey[formViewType]]

    if (!renderComponent || disabledIds.includes(item.id)) {
      return null
    }

    let sfObject, connectedFieldDetails
    const { connectedField, connectedObject } = getMainConnected(item)
    if (connectedObject && connectedMap[connectedObject]) {
      sfObject = connectedMap[connectedObject].sfObject
    }
    if (isConnected && connectedField) {
      connectedFieldDetails = extractFormFieldDetails({
        connectedField,
        connectedMap,
        describeMap,
        connectedObject
      })
    }
    if (options) {
      options.forEach((option) => {
        if (option.connectedField) {
          option.connectedFieldDetails = extractFormFieldDetails({
            connectedField: option.connectedField,
            connectedMap,
            describeMap,
            connectedObject
          })
        }
      })
    }
    let label = title
    let helpTextLabel = helpText
    let tooltipLabel = tooltip

    if (conditions && conditions.length > 0) {
      const altLabel = checkAltLabel({
        item,
        describeMap,
        connectedMap,
        values,
        errors: props.baseErrors,
        langVersion,
        elementsMap: props.elementsMap
      })
      if (typeof altLabel === 'string') {
        label = altLabel
      }
      const altHelpText = checkAltLabel({
        item,
        connectedMap,
        describeMap,
        values,
        errors: props.baseErrors,
        langVersion,
        elementsMap: props.elementsMap,
        conditionType: 'altHelpText'
      })
      if (typeof altHelpText === 'string') {
        helpTextLabel = altHelpText
      }
      if (formViewType === 'fillable-pdf' && !formElementsWithoutInput.includes(type)) {
        item.condtionsText = getPDFLabelFromConditions({
          elementsMap: props.elementsMap,
          type,
          idToRenderIndex,
          conditions,
          langVersion,
          connectedFieldDetails
        })
      }
    }
    label = parseFormLabelText({
      text: label,
      langVersion,
      objectsFieldsMap: props.objectsFieldsMap,
      describeMap,
      connectedMap,
      pdf: pdfView,
      returnString: !pdfView
    })
    helpTextLabel = parseFormLabelText({
      text: helpTextLabel,
      langVersion,
      objectsFieldsMap: props.objectsFieldsMap,
      describeMap,
      connectedMap,
      pdf: pdfView,
      returnString: !pdfView
    })

    tooltipLabel = parseFormLabelText({
      text: tooltipLabel,
      langVersion,
      objectsFieldsMap: props.objectsFieldsMap,
      describeMap,
      connectedMap,
      pdf: pdfView,
      returnString: !pdfView
    })
    const parsedLabel = parseLabel(label)
    const parsedPDFLabel = parseLabel(label, true, false, {
      fontSize: pdfDefaultFontSize
    })
    const parsedHelptextLabel = parseLabel(helpTextLabel)
    const parsedPDFHelptextLabel = parseLabel(
      helpTextLabel,
      true,
      false,
      {
        fontSize: pdfDefaultFontSize - 2,
        fontStyle: 'italic',
        marginBottom: 8,
        fontFamily:
          langVersion === 'iu-cans' ? inuktitutFormPdfFontFamily : defaultFormPdfFontFamily
      },
      {
        '*': {
          fontSize: pdfDefaultFontSize - 2,
          fontStyle: 'italic',
          fontFamily:
            langVersion === 'iu-cans' ? inuktitutFormPdfFontFamily : defaultFormPdfFontFamily,
          margin: 0,
          padding: 0
        },
        p: {
          marginBottom: 8
        },
        ul: {
          padding: 0
        },
        li: {
          padding: 0
        }
      }
    )

    const alt = item.altLabelPlacement
    if (renderPrint) {
      return (
        <FormElementForReadOnly
          {...props}
          item={item}
          xs={xs}
          langVersion={langVersion}
          connectedMap={connectedMap}
          describeMap={describeMap}
          muBag={muBag}
          index={index}
          paddingStyles={paddingStyles}
          label={label}
          tooltipLabel={tooltipLabel}
          helptextLabel={parsedHelptextLabel}
          connectedFieldDetails={connectedFieldDetails}
          connectedObject={sfObject}
        />
      )
    }
    const baseElement = renderComponent({
      ...item,
      describeMap,
      disabledIds,
      connectedMap,
      editMode: false,
      connectedObject: sfObject,
      connectedFieldDetails,
      muBag,
      langVersion,
      tooltip: tooltipLabel,
      values,
      renderIndex: renderElementsWithNumbering && idToRenderIndex[item.id],
      pdfDocument,
      ...props
    })
    if (pdfView) {
      const conditionsMet = conditions.filter((condition) => {
        return isConditionMet({
          condition,
          elementsMap: props.elementsMap,
          values,
          langVersion,
          connectedMap,
          errors: props.baseErrors
        }).conditionMet
      })

      return (
        <View
          style={{
            width: props.printWidth || '100%',
            ...paddingStyles
          }}
          wrap={Boolean(allowPDFWrap)}
          break={pageBreak}
        >
          <AdjustPdfProps typeProps={typeProps}>
            {item.condtionsText && type !== 'uploadFiles' ? (
              <View style={{ marginBottom: 8 }}>
                {item.condtionsText.map((text, index) => (
                  <Text
                    key={index}
                    style={{
                      fontSize: pdfDefaultFontSize,
                      fontStyle: 'italic'
                    }}
                  >
                    {text}
                  </Text>
                ))}
              </View>
            ) : (
              <View />
            )}
            {formViewType.includes('pdf') && !formPDFElementsWithoutDefaultTitle.includes(type) && (
              <FormElementTitlePDF
                helpText={parsedPDFHelptextLabel}
                tooltip={tooltipLabel}
                index={idToRenderIndex[item.id]}
                title={parsedPDFLabel}
                elementType={type}
                formViewType={formViewType}
                showNumering={renderElementsWithNumbering}
                language={langVersion}
                required={required}
                minFiles={typeProps.minFiles}
                conditionsMet={conditionsMet}
              />
            )}
            {tooltipLabel && type !== 'uploadFiles' ? (
              <Text
                style={{
                  fontSize: pdfDefaultFontSize - 2,
                  fontStyle: 'italic',
                  marginBottom: 8
                }}
              >
                {`${tooltipLabel}`}
              </Text>
            ) : (
              <View />
            )}
            {React.cloneElement(baseElement, {
              title: parsedPDFLabel,
              helpText: parsedPDFHelptextLabel
            })}
          </AdjustPdfProps>
        </View>
      )
    }

    return (
      <Grid
        key={index}
        id={item.id}
        item
        container
        xs={xs}
        style={{
          width: '100%',
          display: 'flex',
          ...paddingStyles
        }}
        direction={alt ? 'row' : 'column'}
        className="break-in-print form-parsed-text"
        wrap="nowrap"
      >
        <FormElementTitle
          tooltip={tooltipLabel}
          labelsWidth={labelsWidth}
          id={item.id}
          altLabelPlacement={alt}
          helpText={!elementData.customHelptext && helpTextLabel}
          title={label}
          type={type}
          showFieldLabel={showFieldLabel}
          useMultiuser={props.useMultiuser}
          muBag={muBag}
          disabled={props.disabled}
          connectedObject={sfObject}
          item={item}
          required={required}
        />
        <Grid item xs className="form-parsed-text">
          {React.cloneElement(baseElement, {
            title: parsedLabel,
            helpText: parsedHelptextLabel
          })}
        </Grid>
      </Grid>
    )
  }
}
